import { useState } from "react";
import { StatusMessage } from "../ui";
import type { PortfolioOrder } from "../../types/portfolio";
import { cn } from "../../lib/utils";
import { TeamCodeBadge } from "../ui/TeamCodeBadge";
import { TEAM_IDENTITY_BADGE_SIZE, TEAM_IDENTITY_NAME_CLASS } from "../ui/teamIdentityScale";
import { getWorldCupFlagCountryCode } from "@sportex100/shared/utils/worldCupFlags";
import { isSoccerTradingCompetition } from "@sportex100/shared/utils/competitionIdentity.ts";
import { useLocalization } from "../../lib/i18n";

type OpenOrdersSectionProps = {
  orders: PortfolioOrder[];
  competitionCode: string;
  quantityFormatter: Intl.NumberFormat;
  numberFormatter: Intl.NumberFormat;
  cancellingOrderId: number | null;
  cancelError: string | null;
  onCancelOrder: (orderId: number) => void;
  hasOnCancelOrder: boolean;
  onPromoteOrder?: (order: PortfolioOrder) => void;
  hasOnPromoteOrder?: boolean;
  onEditOrder?: (order: PortfolioOrder) => void;
  hasOnEditOrder?: boolean;
};

function formatShortDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function formatShortDateOnly(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function MobileOrderMetric({
  label,
  value,
  align = "left",
  className,
}: {
  label: string;
  value: string;
  align?: "left" | "right";
  className?: string;
}) {
  return (
    <div className={cn("min-w-0", align === "right" && "text-right", className)}>
      <p className="truncate font-display text-[10px] font-bold uppercase leading-none tracking-[0.08em] text-foreground-faint">
        {label}
      </p>
      <p className="mt-0.5 truncate font-display text-[17px] font-bold leading-none text-foreground tabular-nums">
        {value}
      </p>
    </div>
  );
}

export function OpenOrdersSection({
  orders,
  competitionCode,
  quantityFormatter,
  numberFormatter,
  cancellingOrderId,
  cancelError,
  onCancelOrder,
  hasOnCancelOrder,
  onPromoteOrder,
  hasOnPromoteOrder = false,
  onEditOrder,
  hasOnEditOrder = false,
}: OpenOrdersSectionProps) {
  const { t } = useLocalization();
  const [expandedOrderId, setExpandedOrderId] = useState<number | null>(null);
  const toggleExpandedOrder = (orderId: number) => {
    setExpandedOrderId((current) => (current === orderId ? null : orderId));
  };

  return (
    <>
      {cancelError && <div className="p-3"><StatusMessage variant="error">{cancelError}</StatusMessage></div>}
      
      {orders.length === 0 ? (
        <div className="p-3">
          <StatusMessage>No open orders.</StatusMessage>
        </div>
      ) : (
        <div className="p-2">
          <div>
            <table className="hidden w-full table-fixed">
              <thead className="border-b border-white/10 bg-black/25">
                <tr className="h-10 text-[11px] font-semibold uppercase tracking-[0.14em] text-foreground-muted">
                  <th className="w-[220px] px-3 text-left">Team</th>
                  <th className="w-[80px] px-2 text-left">Side</th>
                  <th className="w-[80px] px-2 text-left">Qty</th>
                  <th className="w-[90px] px-2 text-left">Price</th>
                  <th className="w-[100px] px-2 text-left">Total</th>
                  <th className="w-[150px] px-2 text-left">Created</th>
                  <th className="w-[190px] px-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => {
                  const isCancelling = cancellingOrderId === order.trade_order_id;
                  const sideIsBuy = order.side === "BUY";
                  const teamName =
                    (order as PortfolioOrder & { team_name?: string }).team_name ?? order.team_code;
                  const worldCupFlagCountryCode =
                    isSoccerTradingCompetition(competitionCode) ? getWorldCupFlagCountryCode(order.team_code) : null;

                  return (
                    <tr
                      key={order.trade_order_id}
                      className="h-12 border-b border-white/[0.08] text-sm transition-colors last:border-b-0 hover:bg-white/[0.03]"
                    >
                      <td className="px-3">
                        <div className="flex min-w-0 items-center gap-2.5">
                          <TeamCodeBadge
                            code={order.team_code}
                            size={
                              worldCupFlagCountryCode
                                ? TEAM_IDENTITY_BADGE_SIZE.portfolio.worldCupTableBadge
                                : TEAM_IDENTITY_BADGE_SIZE.portfolio.tableBadge
                            }
                            primaryColor={order.team_primary_color ?? null}
                            secondaryColor={order.team_secondary_color ?? null}
                            variant={worldCupFlagCountryCode ? "worldCupFlag" : undefined}
                            flagCountryCode={worldCupFlagCountryCode}
                          />
                          <span className="truncate font-semibold text-foreground">{teamName}</span>
                        </div>
                      </td>
                      <td className="px-2">
                        <span
                          className={cn(
                            "inline-flex min-w-[52px] items-center justify-center rounded-pill px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.1em]",
                            sideIsBuy ? "bg-success/20 text-success" : "bg-danger/20 text-danger",
                          )}
                        >
                          {order.side}
                        </span>
                      </td>
                      <td className="px-2 text-foreground">{quantityFormatter.format(order.open_quantity)}</td>
                      <td className="px-2 text-foreground">{numberFormatter.format(order.price)}</td>
                      <td className="px-2 text-foreground">{numberFormatter.format(order.order_value)}</td>
                      <td className="px-2 text-foreground-muted">{formatShortDate(order.created_at)}</td>
                      <td className="px-3 text-right">
                        {(hasOnPromoteOrder || hasOnEditOrder || hasOnCancelOrder) ? (
                          <div className="inline-flex items-center gap-2">
                            {hasOnPromoteOrder && onPromoteOrder && (
                              <button
                                type="button"
                                onClick={() => onPromoteOrder(order)}
                                disabled={isCancelling}
                                className="text-xs font-semibold text-brand transition hover:text-brand-hover disabled:opacity-60"
                              >
                                {t("portfolio.order.promote")}
                              </button>
                            )}
                            {hasOnEditOrder && onEditOrder && (
                              <button
                                type="button"
                                onClick={() => onEditOrder(order)}
                                disabled={isCancelling}
                                className="text-xs font-semibold text-foreground-muted transition hover:text-brand disabled:opacity-60"
                              >
                                {t("portfolio.order.edit")}
                              </button>
                            )}
                            {hasOnCancelOrder && (
                              <button
                                type="button"
                                onClick={() => onCancelOrder(order.trade_order_id)}
                                disabled={isCancelling}
                                className="text-xs font-semibold text-danger transition hover:text-danger/80 disabled:opacity-60"
                              >
                                {isCancelling ? "Cancelling..." : t("portfolio.order.cancel")}
                              </button>
                            )}
                          </div>
                        ) : (
                          <span className="text-foreground-muted">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div className="w-full space-y-3">
              {orders.map((order) => {
                const isCancelling = cancellingOrderId === order.trade_order_id;
                const sideIsBuy = order.side === "BUY";
                const isExpanded = expandedOrderId === order.trade_order_id;
                const teamName =
                  (order as PortfolioOrder & { team_name?: string }).team_name ?? order.team_code;
                const worldCupFlagCountryCode =
                  isSoccerTradingCompetition(competitionCode) ? getWorldCupFlagCountryCode(order.team_code) : null;

                return (
                  <article
                    key={order.trade_order_id}
                    className={cn(
                      "orderMobileCard orderTabletCard rounded-xl border bg-background-card p-3 shadow-[0_12px_28px_rgba(0,0,0,0.28)] transition-colors md:p-4",
                      sideIsBuy ? "border-success" : "border-danger",
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => toggleExpandedOrder(order.trade_order_id)}
                      aria-expanded={isExpanded}
                      className="grid min-h-11 w-full grid-cols-[auto_minmax(0,1fr)] items-start gap-3 text-left md:grid-cols-[auto_minmax(16rem,1.8fr)_minmax(3.8rem,0.38fr)_minmax(4.4rem,0.48fr)_minmax(5.4rem,0.58fr)_auto] md:items-center md:gap-4"
                    >
                      <div className="self-start">
                        <TeamCodeBadge
                          code={order.team_code}
                          size={TEAM_IDENTITY_BADGE_SIZE.portfolio.cardBadge}
                          primaryColor={order.team_primary_color ?? null}
                          secondaryColor={order.team_secondary_color ?? null}
                          variant={worldCupFlagCountryCode ? "worldCupFlag" : undefined}
                          flagCountryCode={worldCupFlagCountryCode}
                        />
                      </div>

                      <div className="min-w-0">
                        <p className={TEAM_IDENTITY_NAME_CLASS.portfolioCard}>
                          {teamName}
                        </p>
                        <span
                          className={cn(
                            "orderTabletSidePill mt-1 hidden h-[22px] w-fit min-w-12 items-center justify-center rounded px-2 font-display text-[9px] font-bold uppercase tracking-[0.06em] md:inline-flex",
                            sideIsBuy ? "bg-success text-black" : "bg-danger text-black",
                          )}
                        >
                          {t(sideIsBuy ? "portfolio.filters.buy" : "portfolio.filters.sell").toUpperCase()}
                        </span>
                        <div className="mt-1.5 grid grid-cols-[auto_minmax(2.5rem,0.85fr)_minmax(2.2rem,0.6fr)_minmax(2.8rem,0.75fr)_auto] items-end gap-x-2 gap-y-1 md:hidden">
                          <span
                            className={cn(
                              "orderMobileSidePill inline-flex h-[22px] min-w-12 items-center justify-center self-center rounded px-2 font-display text-[9px] font-bold uppercase tracking-[0.06em]",
                              sideIsBuy ? "bg-success text-black" : "bg-danger text-black",
                            )}
                          >
                            {t(sideIsBuy ? "portfolio.filters.buy" : "portfolio.filters.sell").toUpperCase()}
                          </span>
                          <MobileOrderMetric
                            className="md:hidden"
                            label={t("portfolio.metric.date")}
                            value={formatShortDateOnly(order.created_at)}
                          />
                          <MobileOrderMetric
                            label={t("portfolio.metric.qty")}
                            value={quantityFormatter.format(order.open_quantity)}
                          />
                          <MobileOrderMetric
                            align="right"
                            label={t("portfolio.metric.price")}
                            value={numberFormatter.format(order.price)}
                          />
                          <span className="pb-1 font-display text-lg leading-none text-foreground-muted" aria-hidden="true">
                            {isExpanded ? "▴" : "▾"}
                          </span>
                        </div>
                      </div>
                      <MobileOrderMetric
                        className="hidden md:block"
                        label={t("portfolio.metric.qty")}
                        value={quantityFormatter.format(order.open_quantity)}
                      />
                      <MobileOrderMetric
                        className="hidden md:block"
                        align="right"
                        label={t("portfolio.metric.price")}
                        value={numberFormatter.format(order.price)}
                      />
                      <MobileOrderMetric
                        className="hidden md:block"
                        align="right"
                        label={t("portfolio.metric.total")}
                        value={numberFormatter.format(order.order_value)}
                      />
                      <span className="hidden font-display text-lg leading-none text-foreground-muted md:flex md:min-h-11 md:w-11 md:items-center md:justify-center md:justify-self-end" aria-hidden="true">
                        {isExpanded ? "▴" : "▾"}
                      </span>
                    </button>

                    {isExpanded && (hasOnPromoteOrder || hasOnEditOrder || hasOnCancelOrder) && (
                      <div className="orderActionsPanelMobile mt-3 grid grid-cols-3 gap-2 border-t border-white/[0.08] pt-3">
                        {hasOnPromoteOrder && onPromoteOrder && (
                          <button
                            type="button"
                            onClick={() => onPromoteOrder(order)}
                            disabled={isCancelling}
                            className="min-h-12 rounded-md bg-blue-300 px-2 font-display text-[13px] font-bold uppercase tracking-[0.08em] text-black transition hover:bg-blue-200 disabled:opacity-60"
                          >
                            {t("portfolio.order.promote")}
                          </button>
                        )}
                        {hasOnEditOrder && onEditOrder && (
                          <button
                            type="button"
                            onClick={() => onEditOrder(order)}
                            disabled={isCancelling}
                            className="min-h-12 rounded-md bg-brand px-2 font-display text-[13px] font-bold uppercase tracking-[0.08em] text-black transition hover:bg-brand-hover disabled:opacity-60"
                          >
                            {t("portfolio.order.edit")}
                          </button>
                        )}
                        {hasOnCancelOrder && (
                          <button
                            type="button"
                            onClick={() => onCancelOrder(order.trade_order_id)}
                            disabled={isCancelling}
                            className="min-h-12 rounded-md bg-danger px-2 font-display text-[13px] font-bold uppercase tracking-[0.08em] text-black transition hover:bg-danger/90 disabled:opacity-60"
                          >
                            {isCancelling ? "Cancelling..." : t("portfolio.order.cancel")}
                          </button>
                        )}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
